import type { DateRange } from 'react-day-picker';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/lib/cn';
import { format } from 'date-fns';
import { ClockIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { TimeInput, TimeInputItem } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle';

import type {
  DateTimePickerCalendarProps,
  DateTimePickerClearButtonProps,
  DateTimePickerMode,
  DateTimePickerModeSelectorProps,
  DateTimePickerTimeInputProps,
  DateTimeRangeValue,
  DateTimeValue,
} from './DateTimePicker.type';

import { getCurrentTime, isValidTime, useDateTimePicker } from './DateTimePickerContext';

export function DateTimePickerModeSelector(dateTimePickerModeSelectorProps: DateTimePickerModeSelectorProps) {
  const {
    labels = {
      start: 'Tylko początek',
      end: 'Tylko koniec',
      range: 'Zakres',
    },
    modes = ['start', 'end', 'range'],
  } = dateTimePickerModeSelectorProps;

  const { mode, setMode, onRangeChange, rangeValue } = useDateTimePicker();

  const handleModeChange = useCallback(
    (newMode: string) => {
      if (!newMode) return;
      const validMode = newMode as DateTimePickerMode;
      setMode(validMode);

      // When switching to single-selection modes, preserve the relevant date
      if (validMode === 'start' && rangeValue?.to?.date) {
        onRangeChange?.({
          from: rangeValue.from,
          to: undefined,
        });
      } else if (validMode === 'end' && rangeValue?.from?.date) {
        onRangeChange?.({
          from: undefined,
          to: rangeValue.to,
        });
      }
    },
    [setMode, onRangeChange, rangeValue]
  );

  return (
    <div className="flex flex-col gap-3">
      <ToggleGroup type={'single'} value={mode} onValueChange={handleModeChange} variant={'outline'} size={'md'} groupVariant={'default'} alwaysOneSelected={true} className={'w-full sm:w-auto'}>
        {modes.includes('start') && <ToggleGroupItem value={'start'}>{labels.start}</ToggleGroupItem>}
        {modes.includes('range') && <ToggleGroupItem value={'range'}>{labels.range}</ToggleGroupItem>}
        {modes.includes('end') && <ToggleGroupItem value={'end'}>{labels.end}</ToggleGroupItem>}
      </ToggleGroup>
    </div>
  );
}

export function DateTimePickerCalendar(dateTimePickerCalendarProps: DateTimePickerCalendarProps) {
  const { className, numberOfMonths: overrideMonths } = dateTimePickerCalendarProps;

  const { mode, value, onChange, rangeValue, onRangeChange, numberOfMonths: contextMonths, matcher } = useDateTimePicker();

  const months: number = overrideMonths ?? contextMonths;

  // Convert to DateRange for Calendar component
  const dateRange = useMemo<DateRange | undefined>(() => {
    if (!rangeValue?.from?.date && !rangeValue?.to?.date) return undefined;
    return {
      from: rangeValue?.from?.date,
      to: rangeValue?.to?.date,
    };
  }, [rangeValue?.from?.date, rangeValue?.to?.date]);

  const handleRangeSelect = useCallback(
    (newDateRange: DateRange | undefined) => {
      if (!onRangeChange) return;

      const currentTime = getCurrentTime();

      const newRangeValue: DateTimeRangeValue = {
        from: newDateRange?.from
          ? {
              date: newDateRange.from,
              time: rangeValue?.from?.time && isValidTime(rangeValue.from.time) ? rangeValue.from.time : currentTime,
            }
          : undefined,
        to: newDateRange?.to
          ? {
              date: newDateRange.to,
              time: rangeValue?.to?.time && isValidTime(rangeValue.to.time) ? rangeValue.to.time : currentTime,
            }
          : undefined,
      };

      onRangeChange(newRangeValue);
    },
    [onRangeChange, rangeValue]
  );

  const handleSingleSelect = useCallback(
    (date: Date | undefined) => {
      const currentTime = getCurrentTime();

      if (mode === 'start') {
        onRangeChange?.({
          from: date
            ? {
                date,
                time: rangeValue?.from?.time && isValidTime(rangeValue.from.time) ? rangeValue.from.time : currentTime,
              }
            : undefined,
          to: undefined,
        });
      } else if (mode === 'end') {
        onRangeChange?.({
          from: undefined,
          to: date
            ? {
                date,
                time: rangeValue?.to?.time && isValidTime(rangeValue.to.time) ? rangeValue.to.time : currentTime,
              }
            : undefined,
        });
      }
    },
    [mode, onChange, onRangeChange, value, rangeValue]
  );

  if (mode === 'range') {
    return (
      <div className={cn('flex justify-center p-2', className)}>
        <Calendar
          mode="range"
          fixedWeeks={true}
          showOutsideDays={false}
          defaultMonth={rangeValue?.from?.date || new Date()}
          selected={dateRange}
          onSelect={handleRangeSelect}
          numberOfMonths={months}
          disabled={matcher}
          classNames={{
            months: 'relative flex flex-col gap-3 sm:flex-row',
          }}
        />
      </div>
    );
  }

  const selectedDate = mode === 'start' ? rangeValue?.from?.date : rangeValue?.to?.date;

  const defaultMonth = mode === 'start' ? rangeValue?.from?.date || new Date() : rangeValue?.to?.date || new Date();

  return (
    <div className={cn('flex justify-center p-2', className)}>
      <Calendar
        mode="single"
        fixedWeeks={true}
        showOutsideDays={false}
        defaultMonth={defaultMonth}
        selected={selectedDate}
        onSelect={handleSingleSelect}
        numberOfMonths={months}
        disabled={matcher}
        classNames={{
          months: 'relative flex flex-col gap-3 sm:flex-row',
        }}
      />
    </div>
  );
}

export function DateTimePickerTimeInput(dateTimePickerTimeInputProps: DateTimePickerTimeInputProps) {
  const { className, target = 'from', label, showClear = true, showDate = true, dateFormat = 'LLL dd, y', disableWhenNotActive = true } = dateTimePickerTimeInputProps;

  const { mode, value, onChange, rangeValue, onRangeChange } = useDateTimePicker();

  // Determine current value based on target and mode
  const currentValue = useMemo<DateTimeValue | undefined>(() => {
    if (target === 'from') return rangeValue?.from;
    return rangeValue?.to;
  }, [mode, target, value, rangeValue]);

  // Determine if this input should be disabled
  const isDisabled = useMemo<boolean>(() => {
    if (!disableWhenNotActive) return false;
    if (mode === 'start' && target === 'to') return true;
    if (mode === 'end' && target === 'from') return true;
    return false;
  }, [mode, target, disableWhenNotActive]);

  // Determine if this input is active (highlighted)
  const isActive = useMemo<boolean>(() => {
    if (mode === 'start' && target === 'from') return true;
    if (mode === 'end' && target === 'to') return true;
    return false;
  }, [mode, target]);

  // Default labels
  const defaultLabel = useMemo<string>(() => {
    if (label) return label;
    return target === 'from' ? 'Początek' : 'Koniec';
  }, [label, mode, target]);

  const handleTimeChange = useCallback(
    (time: string) => {
      if (!currentValue?.date) return;

      const newRangeValue = { ...rangeValue };
      if (target === 'from') {
        newRangeValue.from = {
          ...newRangeValue.from,
          date: currentValue.date,
          time,
        };
      } else {
        newRangeValue.to = {
          ...newRangeValue.to,
          date: currentValue.date,
          time,
        };
      }
      onRangeChange?.(newRangeValue);
    },
    [mode, target, currentValue, onChange, onRangeChange, rangeValue]
  );

  const handleClear = useCallback((): void => {
    const newRangeValue = { ...rangeValue };
    if (target === 'from') {
      newRangeValue.from = undefined;
    } else {
      newRangeValue.to = undefined;
    }
    onRangeChange?.(newRangeValue);
  }, [mode, target, onChange, onRangeChange, rangeValue]);

  if (!showDate) return null;

  return (
    <div
      className={cn(
        'group flex flex-col gap-2.5 rounded-2xl border p-3',
        isActive ? 'border-brand/75 bg-brand-accent/25' : 'border-border-secondary bg-background',
        isDisabled && 'pointer-events-none opacity-40',
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={cn('flex size-6 items-center justify-center rounded-md transition-colors', isActive ? 'bg-brand text-brand-foreground' : 'bg-card text-muted-foreground')}>
            <ClockIcon className="size-3.5" />
          </div>
          <span className={cn('text-[10px] font-bold tracking-wider uppercase transition-colors', isActive ? 'text-brand' : 'text-muted-foreground')}>{defaultLabel}</span>
        </div>
        {showClear && currentValue?.date && (
          <Button type="button" variant="ghost" size="sm" onClick={handleClear} className="text-muted-foreground h-6 min-h-6 px-1.5 text-xs font-medium">
            Wyczyść
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5 overflow-hidden">
          <p className="text-foreground truncate text-sm font-semibold">{currentValue?.date ? format(currentValue.date, dateFormat) : 'Wybierz datę'}</p>
          <p className="text-muted-foreground text-xs">{currentValue?.date ? 'Data została wybrana' : 'Oczekiwanie na wybór'}</p>
        </div>

        <TimeInput disabled={isDisabled || !currentValue?.date} value={currentValue?.time || ''} onChange={handleTimeChange} className="w-24">
          <TimeInputItem picker="hours" className="text-sm" />
          <TimeInputItem picker="minutes" className="text-sm" />
        </TimeInput>
      </div>
    </div>
  );
}

export function DateTimePickerClearButton(dateTimePickerClearButtonProps: DateTimePickerClearButtonProps) {
  const { asChild, children, ...props } = dateTimePickerClearButtonProps;

  const { onChange, onRangeChange, rangeValue, mode } = useDateTimePicker();

  const handleClear = useCallback(() => {
    onRangeChange?.({ from: undefined, to: undefined });
  }, [mode, onChange, onRangeChange, rangeValue]);

  if (asChild) {
    return <Slot>{children}</Slot>;
  }

  return (
    <Button type="button" variant="ghost" size="sm" onClick={handleClear} {...props}>
      Wyczyść wszystko
    </Button>
  );
}
